import { forwardRef, ImgHTMLAttributes } from 'react';
import { clsx } from 'clsx';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src?: string | null;
  name?: string;
  size?: AvatarSize;
  showBorder?: boolean;
  status?: 'online' | 'offline' | 'away';
}

const sizeStyles: Record<AvatarSize, { container: string; text: string; status: string }> = {
  xs: { container: 'w-6 h-6', text: 'text-xs', status: 'w-2 h-2' },
  sm: { container: 'w-8 h-8', text: 'text-sm', status: 'w-2.5 h-2.5' },
  md: { container: 'w-10 h-10', text: 'text-base', status: 'w-3 h-3' },
  lg: { container: 'w-14 h-14', text: 'text-xl', status: 'w-3.5 h-3.5' },
  xl: { container: 'w-20 h-20', text: 'text-2xl', status: 'w-4 h-4' },
};

const statusColors = {
  online: 'bg-mint-200',
  offline: 'bg-charcoal-300',
  away: 'bg-coral-300',
};

// Generate initials from name
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Generate a consistent color based on name
function getColorFromName(name: string): string {
  const colors = [
    'bg-primary-300',
    'bg-coral-200',
    'bg-mint-200',
    'bg-primary-200',
    'bg-coral-300',
    'bg-mint-300',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      name = 'User',
      size = 'md',
      showBorder = false,
      status,
      className,
      alt,
      ...props
    },
    ref
  ) => {
    const sizeStyle = sizeStyles[size];
    const initials = getInitials(name);
    const bgColor = getColorFromName(name);

    return (
      <div
        ref={ref}
        className={clsx(
          'relative inline-flex items-center justify-center rounded-full',
          sizeStyle.container,
          showBorder && 'ring-2 ring-white',
          className
        )}
      >
        {src ? (
          <img
            src={src}
            alt={alt || name}
            className={clsx(
              'rounded-full object-cover',
              sizeStyle.container
            )}
            {...props}
          />
        ) : (
          <div
            className={clsx(
              'flex items-center justify-center rounded-full font-semibold text-charcoal-600',
              sizeStyle.container,
              sizeStyle.text,
              bgColor
            )}
          >
            {initials}
          </div>
        )}

        {/* Status indicator */}
        {status && (
          <span
            className={clsx(
              'absolute bottom-0 right-0 rounded-full ring-2 ring-white',
              sizeStyle.status,
              statusColors[status]
            )}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

// Avatar Group
interface AvatarGroupProps {
  avatars: { src?: string; name: string }[];
  max?: number;
  size?: AvatarSize;
  className?: string;
}

export const AvatarGroup = ({
  avatars,
  max = 4,
  size = 'md',
  className,
}: AvatarGroupProps) => {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <div className={clsx('flex -space-x-2', className)}>
      {visibleAvatars.map((avatar, index) => (
        <Avatar
          key={index}
          src={avatar.src}
          name={avatar.name}
          size={size}
          showBorder
          className="relative"
          style={{ zIndex: visibleAvatars.length - index }}
        />
      ))}
      {remainingCount > 0 && (
        <div
          className={clsx(
            'flex items-center justify-center rounded-full bg-cream-300 text-charcoal-600 font-medium ring-2 ring-white',
            sizeStyles[size].container,
            sizeStyles[size].text
          )}
          style={{ zIndex: 0 }}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

export default Avatar;
